import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { NOT_FOUND } from '@src/constants';
import { AppMetadataByAppNameDocument, AppPagesDataByAppNameDocument, BffFacility, LandingPageByAppNameWithPathDocument, PageFragment } from '@src/lib/__generated/sdk';
import { STATES_BY_CODE } from '@src/lib/shared-components-web';
import { buildPageConfig, getAppData, LocationDirectoryPageShell, resolveFacilityDetailsBasePath } from '@src/pages/locations/location-page-shared';
import { getBffFacilitiesByStateAndCity } from '@src/services/bffFacilityCalls';
import { callsByBrand } from '@src/services/callsByBrand';
import { TCity } from '@src/services/getLocations';
import { interpolateContent, PageData } from '@utils/contentVariableInsertion';
import { capitalizeFirstCharacter, formatStateBreadcrumb, getBeffeGraphQlUrl, getEnrichedPageContents, getRootLevelConfig, getUserCoordinates } from '@utils/pages/';
import choosePreview from '@utils/pages/choose-preview';
import { describeContentfulError, throwIfContentfulUnavailable } from '@utils/pages/contentful-errors';
import getConfigServices from '@utils/pages/get-config-services';
import { getDeviceTypeFromHeaders } from '@utils/pages/get-device-type-from-headers';
import getHostFromHeaders from '@utils/pages/get-host-from-headers';
import { getServerSideTranslations } from '@utils/pages/get-serverside-translations';
import { handlePageLoadFailure } from '@utils/pages/handle-page-load-failure';
import setBlogCacheControl from '@utils/pages/set-blog-cache-control/set-blog-cache-control';
const CityPage = (props: Readonly<InferGetServerSidePropsType<typeof getServerSideProps>>) => {
  return <LocationDirectoryPageShell {...props as any} data-sentry-element="LocationDirectoryPageShell" data-sentry-component="CityPage" data-sentry-source-file="index.page.tsx" />;
};
const getCityPage = ({
  pageData,
  facilityDetailsBasePath,
  stateName,
  cityName,
  logger
}: {
  pageData: any;
  facilityDetailsBasePath: string;
  stateName: string;
  cityName: string;
  logger: any;
}) => {
  throwIfContentfulUnavailable(pageData, `CONTENTFUL_WARN_DIRECTORY_STATE_CITY_PAGE - /${facilityDetailsBasePath}/[state]/[city]/`);
  if (pageData.error) {
    logger.warn(`CONTENTFUL_WARN_DIRECTORY_STATE_CITY_PAGE: ${describeContentfulError(pageData.error)} - /${facilityDetailsBasePath}/[state]/[city]/`);
  }
  const page = pageData.data?.pageCollection?.items[0] as PageFragment;
  logger.info(`/${facilityDetailsBasePath}/${stateName}/${cityName}/`);
  return page;
};
const buildCityBreadcrumbs = ({
  facilityDetailsBasePath,
  stateName,
  cityName
}: {
  facilityDetailsBasePath: string;
  stateName: string;
  cityName: string;
}) => {
  return {
    breadcrumbTitle: capitalizeFirstCharacter(cityName),
    breadcrumbsCollection: {
      items: [{
        breadcrumbTitle: 'Home',
        path: '/'
      }, {
        breadcrumbTitle: capitalizeFirstCharacter(facilityDetailsBasePath),
        path: `/${facilityDetailsBasePath}/`
      }, {
        breadcrumbTitle: formatStateBreadcrumb(stateName),
        path: `/${facilityDetailsBasePath}/${stateName}/`
      }]
    }
  };
};
export const getServerSideProps: GetServerSideProps = async ({
  resolvedUrl,
  params,
  locale,
  req,
  res,
  preview = false
}) => {
  const domain = getHostFromHeaders(req.headers);
  preview = choosePreview(domain, preview);
  const deviceType = getDeviceTypeFromHeaders(req.headers);
  const {
    logger,
    brandConfig,
    gqlClient,
    appName
  } = getRootLevelConfig({
    req,
    preview,
    loggerName: 'locations-city'
  });
  const facilityDetailsBasePath = resolveFacilityDetailsBasePath(brandConfig);
  if (typeof params?.state !== 'string' || typeof params?.city !== 'string') {
    logger.warn(`MISSING_STATE_OR_CITY_PARAM: params.state or params.city is not a string, params=${JSON.stringify(params)}`);
    return NOT_FOUND;
  }
  const stateName = params.state;
  const cityName = params.city;
  const pageReferrer = req.headers.referer || '';
  const stateCodeFromHeaders = req.headers['cloudfront-viewer-country-region'] || '';
  try {
    const [appPagesData, appMetadata, pageDataPrimary, translations] = await Promise.all([gqlClient.query(AppPagesDataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query(AppMetadataByAppNameDocument, {
      appName,
      preview
    }).toPromise(), gqlClient.query(LandingPageByAppNameWithPathDocument, {
      appName,
      path: `/${facilityDetailsBasePath}/[state]/[city]/`,
      preview
    }).toPromise(), getServerSideTranslations(locale)]);
    const pageData = pageDataPrimary;
    const appData = getAppData({
      appPagesData,
      appMetadata,
      logger,
      appPagesWarningContext: 'CONTENTFUL_WARN_DIRECTORY_CITY_PAGE'
    });
    if (!appData) {
      logger.warn('APPDATA_NOT_FOUND');
      return NOT_FOUND;
    }
    if (!appData.featureFlags) {
      logger.warn('FEATURE_FLAGS_NOT_FOUND_IN_APPDATA');
      return NOT_FOUND;
    }
    const page = getCityPage({
      pageData,
      facilityDetailsBasePath,
      stateName,
      cityName,
      logger
    });
    if (!page) {
      logger.warn(`COULD_NOT_FIND_CITY_STATE_PAGE`);
      return NOT_FOUND;
    }
    const transformedServices = getConfigServices(brandConfig as any);
    const offices = await getOfficesForCity({
      appData,
      brandConfig,
      stateName,
      cityName,
      transformedServices,
      logger
    });
    const enrichedPageContents = await getEnrichedPageContents({
      page,
      gqlClient,
      preview,
      logger
    });
    if (!offices || offices.length === 0) {
      return NOT_FOUND;
    }
    let locationString = 'Your location';
    if (Array.isArray(offices) && offices.length && offices[0].location) {
      const {
        city,
        state
      } = offices[0].location;
      locationString = `${city}, ${state}`;
    }
    const pageStructure = {
      ...page,
      contentsCollection: {
        items: enrichedPageContents
      }
    };
    const interpolatedPageData = interpolateContent(pageStructure as unknown as PageData, [{
      value: locationString,
      regex: /\[city, state\]/g
    }, {
      value: locationString,
      regex: /\[city\], \[state\]/g
    }, {
      value: locationString,
      regex: /\[city\] \[state\]/g
    }, {
      value: locationString,
      regex: /\[location\]/g
    }, {
      value: stateName,
      regex: /\[state-slug\]/g
    }, {
      value: cityName,
      regex: /\[city-slug\]/g
    }]);
    const breadcrumbsData = buildCityBreadcrumbs({
      facilityDetailsBasePath,
      stateName,
      cityName
    });
    setBlogCacheControl(res, preview);
    return {
      props: {
        ...translations,
        preview,
        env: {
          ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME
        },
        config: buildPageConfig({
          brandConfig,
          facilityDetailsBasePath,
          appData,
          transformedServices,
          req
        }),
        page: interpolatedPageData,
        appData,
        user: getUserCoordinates(req, logger),
        breadcrumbsData,
        cities: offices,
        deviceType,
        pageReferrer,
        stateCodeFromHeaders,
        domain: domain ?? ''
      }
    };
  } catch (e) {
    return handlePageLoadFailure(logger, resolvedUrl, e);
  }
};
export default CityPage;
async function getOfficesForCity({
  appData,
  brandConfig,
  stateName,
  cityName,
  transformedServices,
  logger
}: {
  appData: any;
  brandConfig: any;
  stateName: string;
  cityName: string;
  transformedServices: unknown;
  logger: any;
}): Promise<TCity[]> {
  if (appData.featureFlags?.useFacilityBeffeDataSource) {
    const beffeGraphQlUrl = getBeffeGraphQlUrl(transformedServices as any);
    const officesResponse = await getBffFacilitiesByStateAndCity(brandConfig.bffBrandType, stateName.toUpperCase(), capitalizeFirstCharacter(cityName), beffeGraphQlUrl);
    const facilities: BffFacility[] = officesResponse?.data?.facilitiesByStateAndCity || [];
    const offices = facilities.map(facility => {
      const fullStateName = STATES_BY_CODE?.[facility?.address?.stateCode || 'HI'];
      return {
        displayName: facility?.name || '',
        city: facility?.address?.city || '',
        state: fullStateName,
        stateAbbreviation: facility?.address?.stateCode || 'HI',
        location: {
          street1: facility.address?.address1 || '',
          street2: facility.address?.address2 || '',
          city: facility.address?.city || '',
          state: facility.address?.stateCode || '',
          zipCode: facility.address?.zipCode || ''
        }
      };
    });
    logger.info(offices);
    return offices;
  }
  const getOfficesByCityStateFn = callsByBrand[brandConfig.themeId]?.getOfficesByCityState;
  if (!getOfficesByCityStateFn) {
    logger.warn(`GET_OFFICES_BY_CITY_STATE_NOT_FOUND for themeId: ${brandConfig.themeId}`);
    return [];
  }
  return getOfficesByCityStateFn(stateName, cityName.replaceAll('-', '+'));
}